import React from 'react';
import { MealInfo } from '../daily-tile';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import MealItemTile from './MealItemTile';

interface AllMealTypesDisplayProps {
  meals: MealInfo[];
  isLoading: boolean;
  onShowAllMeals: () => void;
  heightMode?: 'fixed' | 'flexible';
  showReviewButton?: boolean;
  isYesterday?: boolean;
}

const AllMealTypesDisplay: React.FC<AllMealTypesDisplayProps> = ({
  meals,
  isLoading,
  onShowAllMeals,
  heightMode = 'flexible',
  showReviewButton = false,
  isYesterday = false
}) => {
  if (isLoading) {
    return <p className="text-sm text-muted-foreground animate-pulse">Loading...</p>;
  }

  // Group meals by type
  const mealsByType = {
    breakfast: meals.filter(meal => meal.mealType === 'breakfast'),
    lunch: meals.filter(meal => meal.mealType === 'lunch'),
    dinner: meals.filter(meal => meal.mealType === 'dinner')
  };

  const totalMeals = meals.length;
  
  // Smart distribution for flexible mode - max 3 total meals across all types
  const getSmartDistribution = () => {
    if (heightMode === 'fixed') {
      return {
        maxItemsPerType: 1,
        maxVisibleTypes: 2,
        showViewAllButton: totalMeals > 2 || Object.values(mealsByType).some(typeMeals => typeMeals.length > 1)
      };
    }
    
    // Flexible mode: smart distribution with max 3 total items
    const typesWithMeals = (['breakfast', 'lunch', 'dinner'] as const).filter(type => mealsByType[type].length > 0);
    const maxTotalItems = 3;
    
    if (typesWithMeals.length === 1) {
      return { maxItemsPerType: maxTotalItems, maxVisibleTypes: 3, showViewAllButton: false };
    } else if (typesWithMeals.length === 2) {
      return { maxItemsPerType: 2, maxVisibleTypes: 3, showViewAllButton: false };
    } else {
      return { maxItemsPerType: 1, maxVisibleTypes: 3, showViewAllButton: false };
    }
  };

  const { maxItemsPerType, maxVisibleTypes, showViewAllButton } = getSmartDistribution();

  const renderMealTypeSection = (
    type: 'breakfast' | 'lunch' | 'dinner', 
    label: string, 
    typeMeals: MealInfo[]
  ) => {
    if (typeMeals.length === 0) return null;

    const isCompact = heightMode === 'fixed';
    const displayMeals = typeMeals.slice(0, maxItemsPerType);
    const hasMore = typeMeals.length > maxItemsPerType;

    return (
      <div key={type} className="space-y-1">
        <div className={`flex items-center gap-2 ${isCompact ? 'mb-0.5' : 'mb-1'}`}>
          <h4 className={`font-medium text-muted-foreground ${isCompact ? 'text-xs' : 'text-sm'}`}>
            {label}
          </h4>
          {typeMeals.length > 1 && (
            <Badge variant="secondary" className="text-xs px-1.5 py-0 h-4">
              {typeMeals.length}
            </Badge>
          )}
        </div>
        
        <div className={`space-y-1 ${isCompact ? 'space-y-0.5' : ''}`}>
          {displayMeals.map(meal => (
            <MealItemTile 
              key={`${meal.type}-${meal.id}`} 
              meal={meal} 
              showReviewButton={showReviewButton}
              isYesterday={isYesterday}
            />
          ))}
          {hasMore && (
            <div className="px-2 py-1">
              <button
                onClick={onShowAllMeals}
                className="text-xs text-muted-foreground hover:text-primary transition-colors cursor-pointer"
              >
                +{typeMeals.length - maxItemsPerType} more...
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (totalMeals === 0) {
    return (
      <div className="flex items-center justify-center text-sm text-muted-foreground h-full">
        No meals planned
      </div>
    );
  }

  // For fixed height mode, prioritize meal types and limit display
  const mealTypeOrder = ['breakfast', 'lunch', 'dinner'] as const;
  const typesWithMeals = mealTypeOrder.filter(type => mealsByType[type].length > 0);
  const displayTypes = heightMode === 'fixed' ? typesWithMeals.slice(0, maxVisibleTypes) : typesWithMeals;

  return (
    <div className={`w-full ${heightMode === 'fixed' ? 'h-full' : ''} flex flex-col`}>
      <div className={`space-y-3 ${heightMode === 'fixed' ? 'space-y-1.5 flex-1 overflow-hidden' : ''}`}>
        {displayTypes.map(type => renderMealTypeSection(type, type.charAt(0).toUpperCase() + type.slice(1), mealsByType[type]))}
      </div>
      
      {showViewAllButton && (
        <div className={`${heightMode === 'fixed' ? 'mt-2' : 'mt-3'} flex-shrink-0`}>
          <Button
            variant="ghost"
            size="sm"
            onClick={onShowAllMeals}
            className="text-xs h-7 px-2 w-full"
          >
            View All ({totalMeals})
          </Button>
        </div>
      )}
    </div>
  );
};

export default AllMealTypesDisplay;